import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";

import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const ARTICLE = getArticleBySlug("/learn/ai-search-geo");
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/ai-search-geo")({
  head: () => ({
    meta: [
        { name: "description", content: "How ChatGPT, Perplexity, and Google AI Overviews are reshaping local search for Colorado businesses, and what to do about it in 2026 and beyond." },
      {
        title: "AI Search & GEO: What ChatGPT Means for Local SEO",
      },
      {
        name: "description",
        content:
          "Generative Engine Optimization (GEO) explained without hype — what's changing for Colorado small businesses, what isn't, and what to do in 2026.",
      },
      {
        property: "og:title",
        content: "AI Search & GEO Explained for Colorado Small Businesses",
      },
      {
        property: "og:description",
        content:
          "Honest, no-hype guide to ChatGPT, Perplexity, and Google AI Overviews — and what to do about them.",
      },
      { property: "og:url", content: `${SITE_URL}/learn/ai-search-geo` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "Colorado Web Impressions",
        description: ARTICLE?.desc ?? "Honest answers from a 25-year Colorado agency.",
      }),
    ],
    links: [{ rel: "canonical", href: "https://coloradowebimpressions.com/learn/ai-search-geo" }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="AI & GEO"
      read="10 min read"
      title="AI search is here. What ChatGPT, Perplexity, and Google AI mean for your local business"
      intro="Half the agencies on the internet are screaming that &ldquo;SEO is dead&rdquo; because of AI. The other half are pretending nothing changed. Both are wrong. Here&rsquo;s the calm, plain-English version."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "AI search & GEO" },
      ]}
      path="/learn/ai-search-geo"
      faqs={[
        { q: "What is GEO (Generative Engine Optimization)?", a: "GEO is optimizing your website and content so AI tools like ChatGPT, Google AI Overviews, and Perplexity cite you as a source when answering questions in your industry." },
        { q: "Is GEO replacing traditional SEO?", a: "No — it's layering on top of it. The same fundamentals (clear content, real expertise, fast site, structured data) feed both. GEO adds emphasis on direct answers, citations, and entity clarity." },
        { q: "How do I know if AI tools are recommending my business?", a: "Ask them. Run real prompts a customer would use ('best plumber in Colorado Springs') in ChatGPT, Gemini, and Perplexity each month and track who gets named. We do this for every client." },
      ]}
    >
      <h2>What actually changed</h2>
      <p>
        In 2024–2025, three things started eating into traditional Google search behavior:
      </p>
      <ul>
        <li><strong>Google AI Overviews</strong> — the AI-generated summary at the top of many search results.</li>
        <li><strong>ChatGPT, Claude, Gemini, Perplexity</strong> — people now ask these tools the kinds of questions they used to type into Google.</li>
        <li><strong>Voice + assistant search</strong> — Siri, Alexa, and in-car assistants pulling from AI sources.</li>
      </ul>

      <h2>What didn&rsquo;t change</h2>
      <ul>
        <li><strong>People still need plumbers right now.</strong> Urgent local intent (&ldquo;water heater leaking Colorado Springs&rdquo;) still goes straight to Google Maps and a phone call. AI doesn&rsquo;t fix burst pipes.</li>
        <li><strong>Google Business Profile still drives the calls.</strong> AI Overviews don&rsquo;t replace the 3-pack for local service queries — they sit above informational content, not the map.</li>
        <li><strong>Reviews, NAP, citations, schema</strong> — all still feed both Google and the AI tools. The AI tools cite the same data sources.</li>
      </ul>

      <h2>What &ldquo;GEO&rdquo; actually means</h2>
      <p>
        Generative Engine Optimization (GEO) is the work of getting your business <em>cited</em> by
        AI search tools, not just ranked by Google. The big levers:
      </p>
      <ol>
        <li><strong>Clear, factual, well-structured content</strong> — AI tools love content that directly answers a question in a self-contained paragraph.</li>
        <li><strong>Structured data (schema)</strong> — Tells the AI exactly what your business is, where, hours, services, prices, ratings.</li>
        <li><strong>Authoritative mentions</strong> — When the Colorado Springs Gazette, the local Chamber, or an industry publication mentions you, AI tools weight that heavily.</li>
        <li><strong>Reviews that include real keywords</strong> — &ldquo;They fixed our furnace in Monument the same day&rdquo; trains the AI on what you do, where, and how.</li>
      </ol>

      <h2>What to do in 2026 (the short list)</h2>
      <ul>
        <li>Add a real <strong>FAQ section</strong> to every service page. AI tools often quote the answer verbatim.</li>
        <li>Make sure your <strong>schema</strong> includes LocalBusiness, Service, FAQPage, and Review markup.</li>
        <li>Get cited in local Colorado publications and directories — even small ones.</li>
        <li>Keep your <strong>Google Business Profile</strong> spotless — it&rsquo;s a primary source for AI tools.</li>
        <li>Don&rsquo;t panic-rebuild your site. The fundamentals from{" "}
          <Link to="/learn/local-seo-guide" className="font-bold text-brand-gold-deep underline">
            our local SEO guide
          </Link>{" "}
          still apply.</li>
      </ul>

      <h2>What NOT to do</h2>
      <ul>
        <li>Don&rsquo;t pay anyone selling &ldquo;guaranteed ChatGPT placement.&rdquo; That&rsquo;s not a thing.</li>
        <li>Don&rsquo;t mass-publish AI-written blog posts trying to game it. AI tools penalize obviously generated, thin content.</li>
        <li>Don&rsquo;t fire your SEO. Local search isn&rsquo;t going anywhere. It&rsquo;s expanding.</li>
      </ul>

      <h2>The honest summary</h2>
      <p>
        For 95% of Colorado small businesses, GEO in 2026 is not a brand-new strategy — it&rsquo;s
        local SEO done well, with a few extra structural touches. If your agency suddenly wants
        $1,500/month extra to &ldquo;handle GEO,&rdquo; ask them to show you exactly which schema
        they&rsquo;re adding and which AI tools are citing your business. Most can&rsquo;t answer.
      </p>
      <p>
        Want a snapshot of where you actually stand right now? My{" "}
        <Link to="/ai-visibility-audit" className="font-bold text-brand-gold-deep underline">
          AI Visibility Audit
        </Link>{" "}
        runs real customer prompts in ChatGPT, Perplexity, Gemini, and Google AI Overviews and gives you a 90-day roadmap. Or if you'd
        rather we just do the work for you, our{" "}
        <Link to="/geo-ai" className="font-bold text-brand-gold-deep underline">
          GEO &amp; AI service
        </Link>{" "}
        bakes this into every site we build, no upcharge — because most of it is just doing SEO right.
      </p>
    </ArticleLayout>
  );
}
